// The agency edits a rider or invites a new one, and adds the papers, with
// MyGaz Pro's own writes (C18 dispatch_add_rider, dispatch_update_rider,
// dispatch_set_rider_document). The rider then signs up in MyGaz Pro.
import { useState } from 'react';
import { S } from '../lib/strings';
import { cmPhone } from '../lib/phone';
import type { AgencySource, RiderInput } from '../lib/agency-api';
import type { AgRider, DocKind } from '../lib/types';
import { Ic } from '../components/Icon';

const KINDS: DocKind[] = ['headshot', 'id_front', 'id_back', 'licence', 'bike'];

export function RiderEdit({ src, agencyId, rider, onClose, onSaved }: { src: AgencySource; agencyId: string; rider: AgRider | null; onClose(): void; onSaved(): void }) {
  const [f, setF] = useState<RiderInput>({
    name: rider?.name ?? '', phone: rider?.phone ? rider.phone.replace(/^237(\d)(\d{2})(\d{2})(\d{2})(\d{2})$/, '$1 $2 $3 $4 $5') : '', plate: rider?.plate ?? '',
    vehicle: rider?.vehicle === 'car' ? 'car' : 'moto', can_install: rider?.can_install ?? false,
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [missing, setMissing] = useState<DocKind[]>(rider?.missing ?? []);
  const err = (e: unknown) => { const c = String((e as Error)?.message ?? e); return S.agency.edit.errors[c] ?? S.agency.edit.errors.default; };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const p = cmPhone(f.phone);
    if (!f.name.trim()) { setMsg({ ok: false, text: S.agency.edit.errors.name_needed }); return; }
    if (!p) { setMsg({ ok: false, text: S.agency.edit.errors.invalid_phone }); return; }
    setBusy(true); setMsg(null);
    try {
      const input = { ...f, name: f.name.trim(), phone: p, plate: f.plate?.trim() || null };
      if (rider) await src.updateRider(rider.id, input); else await src.addRider(input);
      setMsg({ ok: true, text: rider ? S.agency.edit.saved : S.agency.edit.added });
      onSaved();
      if (!rider) setTimeout(onClose, 900);
    } catch (e2) { setMsg({ ok: false, text: err(e2) }); }
    finally { setBusy(false); }
  };
  const upload = async (kind: DocKind, file: File | undefined) => {
    if (!file || !rider) return;
    setBusy(true); setMsg(null);
    try { await src.uploadDoc(agencyId, rider.id, kind, file); setMissing((m) => m.filter((k) => k !== kind)); setMsg({ ok: true, text: S.agency.edit.uploaded }); onSaved(); }
    catch (e2) { setMsg({ ok: false, text: err(e2) }); }
    finally { setBusy(false); }
  };
  const sms = rider?.phone ? `sms:+${rider.phone}?&body=${encodeURIComponent(S.agency.edit.sms(rider.name.split(' ')[0]))}` : null;

  return (
    <>
      <div className="scrim on" onClick={onClose} />
      <aside className="drawer on ag-edit" role="dialog" aria-modal="true" aria-label={rider ? S.agency.edit.title : S.agency.edit.newTitle}>
        <div className="ag-eh"><h2>{rider ? S.agency.edit.title : S.agency.edit.newTitle}</h2><button className="ibtn" onClick={onClose} aria-label={S.agency.edit.cancel}><Ic n="x" s="s" /></button></div>
        <form onSubmit={save} className="ag-form">
          <label><span className="cap">{S.agency.edit.name}</span><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required maxLength={80} /></label>
          <label><span className="cap">{S.agency.edit.phone}</span><input inputMode="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} disabled={!!rider && rider.state !== 'invited'} placeholder="6XX XX XX XX" /></label>
          <label><span className="cap">{S.agency.edit.plate}</span><input value={f.plate ?? ''} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} maxLength={20} /></label>
          <div className="ag-frow"><span className="cap">{S.agency.edit.vehicle}</span>
            <div className="seg">{(['moto', 'car'] as const).map((v) => <button type="button" key={v} aria-pressed={f.vehicle === v} onClick={() => setF({ ...f, vehicle: v })}>{S.agency.edit[v]}</button>)}</div></div>
          <label className="ag-check"><input type="checkbox" checked={f.can_install} onChange={(e) => setF({ ...f, can_install: e.target.checked })} />{S.agency.edit.installer}</label>
          {!rider && <p className="muted ag-small">{S.agency.edit.inviteNote}</p>}
          <div className="ag-facts"><button type="button" className="btn sm" onClick={onClose}>{S.agency.edit.cancel}</button><button className="btn sm pri" disabled={busy}>{rider ? S.agency.edit.save : S.agency.edit.create}</button></div>
        </form>
        {rider && (
          <div className="ag-papers">
            <h3>{S.agency.edit.papers}</h3>
            {KINDS.map((k) => {
              const has = !missing.includes(k) && (k !== 'bike' || !rider.missing.includes('bike'));
              return (
                <div className="ag-ck" key={k}>
                  <span className={'ag-chk ' + (has && k !== 'bike' ? 'ok' : k === 'bike' ? 'na' : 'no')}><Ic n={has && k !== 'bike' ? 'check' : k === 'bike' ? 'minus' : 'x'} s="xs" /></span>{S.ag.docNames[k]}
                  <label className="btn xs ag-up">{k === 'bike' || missing.includes(k) ? S.agency.edit.add : S.agency.edit.replace}
                    <input type="file" accept="image/jpeg,image/png,image/webp" hidden disabled={busy || rider.consent === 'refused'} onChange={(e) => upload(k, e.target.files?.[0])} /></label>
                </div>
              );
            })}
            {rider.state === 'invited' && sms && <a className="btn sm ag-sms" href={sms}><Ic n="send" s="xs" />{S.agency.edit.resend}</a>}
          </div>
        )}
        {msg && <p className={'ag-msg ' + (msg.ok ? 'ok' : 'bad')} role="status">{msg.text}</p>}
      </aside>
    </>
  );
}
